import React, { useState } from 'react';
import { Opportunity } from '@/api/seoApi';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Target, HelpCircle, FileText, Lightbulb, TrendingUp, Sparkles, MessageCircle } from 'lucide-react';

interface OpportunitiesTableProps {
  opportunities: Opportunity[];
  siteId: string;
}

interface Recommendation {
  fullRecommendation: string;
  contentPlacement: string;
  contentSpecs: string;
  optimizationTactics: string;
  quickWins: string;
  contentOutline: string;
}

export function OpportunitiesTable({ opportunities, siteId }: OpportunitiesTableProps) {
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [loadingRecommendation, setLoadingRecommendation] = useState(false);
  const [recommendationDialogOpen, setRecommendationDialogOpen] = useState(false);

  const getOpportunityIcon = (type: string) => {
    switch (type) {
      case 'featured_snippet':
        return <Target className="h-4 w-4" />;
      case 'paa_question':
        return <HelpCircle className="h-4 w-4" />;
      case 'content_gap':
        return <FileText className="h-4 w-4" />;
      case 'page_two_ranking':
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getOpportunityTypeLabel = (type: string) => {
    switch (type) {
      case 'featured_snippet':
        return 'Featured Snippet';
      case 'paa_question':
        return 'People Also Ask';
      case 'content_gap':
        return 'Content Gap';
      case 'page_two_ranking':
        return 'Page 2 Ranking';
      default:
        return type ? type.replace(/_/g, ' ') : 'Unknown';
    }
  };

  const getPriorityBadge = (score: number | null) => {
    if (!score) {
      return <Badge variant="outline">Unknown</Badge>;
    }

    if (score >= 80) {
      return <Badge className="bg-red-500 hover:bg-red-600">Critical</Badge>;
    }

    if (score >= 60) {
      return <Badge className="bg-orange-500 hover:bg-orange-600">High</Badge>;
    }

    if (score >= 40) {
      return <Badge className="bg-yellow-500 hover:bg-yellow-600">Medium</Badge>;
    }

    return <Badge variant="secondary">Low</Badge>;
  };

  const handleGetAdvice = async (opportunity: Opportunity) => {
    setSelectedOpportunity(opportunity);
    setLoadingRecommendation(true);
    setRecommendationDialogOpen(true);
    setRecommendation(null);

    try {
      const response = await authFetch(
        `${API_URL}/seo/sites/${siteId}/opportunities/${opportunity.id}/recommendation`
      );

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get recommendation');
      }

      setRecommendation(data.data);
    } catch (error) {
      console.error('Error getting recommendation:', error);
      alert('Failed to get recommendation from SEO/AEO Specialist');
      setRecommendationDialogOpen(false);
    } finally {
      setLoadingRecommendation(false);
    }
  };

  if (opportunities.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No opportunities detected yet. Click "Detect New Opportunities" to analyze your SERP data.
      </div>
    );
  }

  return (
    <>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Type</TableHead>
          <TableHead>Keyword / Question</TableHead>
          <TableHead>Current Position</TableHead>
          <TableHead>Impact</TableHead>
          <TableHead>Difficulty</TableHead>
          <TableHead>Priority</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {opportunities.map((opp) => (
          <TableRow key={opp.id}>
            <TableCell>
              <div className="flex items-center gap-2">
                {getOpportunityIcon(opp.opportunity_type)}
                <span className="text-sm">
                  {getOpportunityTypeLabel(opp.opportunity_type)}
                </span>
              </div>
            </TableCell>
            <TableCell>
              <div className="space-y-1">
                <div className="font-medium">{opp.keyword}</div>
                {opp.question && (
                  <div className="text-sm text-muted-foreground">{opp.question}</div>
                )}
                {opp.recommended_content_type && (
                  <div className="text-xs text-muted-foreground">
                    Recommended: {opp.recommended_content_type}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>
              {opp.current_position ? (
                <Badge variant="outline">#{opp.current_position}</Badge>
              ) : (
                <span className="text-muted-foreground text-sm">-</span>
              )}
            </TableCell>
            <TableCell>
              {opp.impact_score !== null ? (
                <div className="space-y-1">
                  <Progress value={opp.impact_score} className="h-2" />
                  <span className="text-xs text-muted-foreground">
                    {opp.impact_score}/100
                  </span>
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">-</span>
              )}
            </TableCell>
            <TableCell>
              {opp.difficulty_score !== null ? (
                <div className="space-y-1">
                  <Progress value={opp.difficulty_score} className="h-2" />
                  <span className="text-xs text-muted-foreground">
                    {opp.difficulty_score}/100
                  </span>
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">-</span>
              )}
            </TableCell>
            <TableCell>{getPriorityBadge(opp.priority_score)}</TableCell>
            <TableCell className="text-right">
              <Button
                size="sm"
                onClick={() => handleGetAdvice(opp)}
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Get Advice
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>

    {/* Recommendation Dialog */}
    <Dialog open={recommendationDialogOpen} onOpenChange={setRecommendationDialogOpen}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            SEO/AEO Specialist Recommendation
          </DialogTitle>
          <DialogDescription>
            Expert advice on how to optimize for: <strong>{selectedOpportunity?.keyword}</strong>
          </DialogDescription>
        </DialogHeader>

        {loadingRecommendation ? (
          <div className="py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-muted-foreground">
              Your SEO/AEO Specialist is analyzing the opportunity and crafting personalized recommendations...
            </p>
          </div>
        ) : recommendation ? (
          <div className="space-y-6">
            {/* Quick Wins */}
            {recommendation.quickWins && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Quick Wins (24-48 hours)
                </h3>
                <div className="text-sm text-green-800 whitespace-pre-wrap">
                  {recommendation.quickWins}
                </div>
              </div>
            )}

            {/* Content Placement Strategy */}
            {recommendation.contentPlacement && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Where to Create Content
                </h3>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted p-4 rounded-lg">
                  {recommendation.contentPlacement}
                </div>
              </div>
            )}

            {/* Content Specifications */}
            {recommendation.contentSpecs && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  Content Specifications
                </h3>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted p-4 rounded-lg">
                  {recommendation.contentSpecs}
                </div>
              </div>
            )}

            {/* Optimization Tactics */}
            {recommendation.optimizationTactics && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Optimization Tactics
                </h3>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted p-4 rounded-lg">
                  {recommendation.optimizationTactics}
                </div>
              </div>
            )}

            {/* Content Outline */}
            {recommendation.contentOutline && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Suggested Content Outline
                </h3>
                <div className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted p-4 rounded-lg font-mono">
                  {recommendation.contentOutline}
                </div>
              </div>
            )}

            {/* Chat with Specialist */}
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-3">
                Want to discuss this recommendation further or explore other opportunities?
              </p>
              <Button variant="outline" className="gap-2">
                <MessageCircle className="h-4 w-4" />
                Chat with SEO/AEO Specialist
              </Button>
            </div>

            {/* Signed by */}
            <div className="text-right text-sm text-muted-foreground italic pt-2 border-t">
              — Your SEO/AEO Specialist
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
    </>
  );
}
